import { ECHOPAY_SVG } from "@/assets/svgs";
import {
  AppNotification,
  NotificationApi,
  NotificationCategory,
  NotificationGroup,
  NotificationType,
  NotificationVisualType,
  TRANSACTION_LINK_TYPES,
} from "@/lib/types/notification.type";

/** Tab order + labels for the notification category tabs. */
export const NOTIFICATION_TABS: {
  value: NotificationCategory;
  label: string;
}[] = [
  { value: NotificationCategory.TRANSACTIONS, label: "Transactions" },
  { value: NotificationCategory.SERVICES, label: "Services" },
  { value: NotificationCategory.ACTIVITIES, label: "Activities" },
  { value: NotificationCategory.PROMOTIONS, label: "Promotions" },
];

/** Cap an unread count for display, e.g. `formatUnreadCount(12)` -> "9+". */
export const formatUnreadCount = (count: number, cap = 9): string =>
  count > cap ? `${cap}+` : `${count}`;

/**
 * Map a backend notification type to a visual bucket (icon + accent colour).
 * Intentionally partial — unlisted / future types fall back to "info" via
 * `getVisualType`, so a new backend type never breaks the build or the UI.
 */
const VISUAL_TYPE_BY_TYPE: Partial<
  Record<NotificationType, NotificationVisualType>
> = {
  transaction_received: "info",
  settlement_completed: "success",
  settlement_failed: "error",
  login: "info",
  kyc_verified: "success",
  kyc_rejected: "error",
  bank_account_added: "success",
  bank_account_default_set: "success",
  wallet_address_created: "success",
  security_alert: "warning",
  system_announcement: "info",
  broadcast: "info",
};

const getVisualType = (type: NotificationType): NotificationVisualType =>
  VISUAL_TYPE_BY_TYPE[type] ?? "info";

const getIcon = (visualType: NotificationVisualType) => {
  switch (visualType) {
    case "success":
      return ECHOPAY_SVG().greenFileIcon();
    case "warning":
    case "error":
      return ECHOPAY_SVG().alarmIcon();
    case "info":
    default:
      return ECHOPAY_SVG().blueBellIcon();
  }
};

/** Convert a raw API notification into the UI view model. */
export const mapNotification = (n: NotificationApi): AppNotification => {
  const visualType = getVisualType(n.type);
  const isTransactionLink =
    TRANSACTION_LINK_TYPES.includes(n.type) && Boolean(n.reference_id);

  return {
    id: n.id,
    category: n.category,
    notificationType: n.type,
    type: visualType,
    icon: getIcon(visualType),
    title: n.title,
    message: n.message,
    timestamp: new Date(n.created_at),
    isRead: n.is_read,
    referenceId: n.reference_id,
    transactionId: isTransactionLink ? n.reference_id ?? undefined : undefined,
    hasDetails: isTransactionLink,
  };
};

/**
 * Group notifications by day, newest group first. "Today" is always first;
 * older groups are labelled with a full date, e.g. "17 December 2025".
 */
export const groupNotificationsByDate = (
  notifications: AppNotification[]
): NotificationGroup[] => {
  const groups: Record<string, AppNotification[]> = {};
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  notifications.forEach((notification) => {
    const notifDate = new Date(notification.timestamp);
    notifDate.setHours(0, 0, 0, 0);

    const daysDiff = Math.floor(
      (today.getTime() - notifDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    const groupKey =
      daysDiff === 0
        ? "Today"
        : notifDate.toLocaleDateString("en-US", {
            day: "numeric",
            month: "long",
            year: "numeric",
          });

    if (!groups[groupKey]) groups[groupKey] = [];
    groups[groupKey].push(notification);
  });

  return Object.entries(groups)
    .sort(([dateA], [dateB]) => {
      if (dateA === "Today") return -1;
      if (dateB === "Today") return 1;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    })
    .map(([date, notifs]) => ({ date, notifications: notifs }));
};
